import React, { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Button, Link, List, Typography } from '@material-ui/core';
import { darken, makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import {
  Network,
  UpdateUserProfileOnboardingMutation,
  UpdateUserProfileOnboardingMutationVariables,
  useUpdateUserProfileOnboardingMutation,
} from 'api';
import {
  ClientLogo,
  ConnectAccount,
  Connection,
  CustomSVG,
  Layouts,
  Loaders,
  PaddedPaper,
} from 'components';
import {
  useCurrentUser,
  useProtectedClient,
  useSocialConnections,
  useNetworkCapabilities,
} from 'hooks';
import { useUpdateCurrentUser } from 'updaters';
import * as paths from 'paths';

import { connectionPopup, getAppImage } from 'helpers';

import { useTrackingQuery } from 'app-react-query/hooks';
import { FunnelEvent } from 'analytics';

type Update = UpdateUserProfileOnboardingMutation;
type UpdateVariables = UpdateUserProfileOnboardingMutationVariables;

const useStyles = makeStyles(theme => ({
  button: {
    padding: theme.spacing(),
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
  twitter: {
    backgroundColor: theme.custom.networks.twitter,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.twitter, 0.1),
    },
  },
  linkedin: {
    backgroundColor: theme.custom.networks.linkedin,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.linkedin, 0.1),
    },
  },
}));

const OnboardingSocials: FC = () => {
  const history = useHistory();
  const client = useProtectedClient();
  const currentUser = useCurrentUser();
  const classes = useStyles();
  const { hasTwitter } = useNetworkCapabilities();
  const { connections } = useSocialConnections();
  const { t } = useTranslation();

  const track = useTrackingQuery();
  const updateOptions = useUpdateCurrentUser<Update, UpdateVariables>();

  const { mutateAsync, isLoading } = useUpdateUserProfileOnboardingMutation({
    onSuccess: data => {
      const socialConnectionNames = connections.map(connection => connection.provider);
      track(FunnelEvent.SLNUSUMCN, data.updateUserProfileOnboarding.id, {
        user_networks: socialConnectionNames,
      });
    },
    ...updateOptions,
  });

  async function handleContinue(event: MouseEvent) {
    event.preventDefault();

    await mutateAsync({
      data: {
        id: currentUser.id,
        last_onboarding_step: 3,
        onboarding_completed: true,
      },
    });

    history.push(paths.setupProfile);
  }

  function renderConnections() {
    return connections.map(account => <Connection key={account.id} account={account} />);
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/board.png', 500)} />;
  }

  function renderMain() {
    return (
      <PaddedPaper disableBorder padding="64px 48px">
        <Layouts.OnboardingBox>
          <Box textAlign="center">
            <ClientLogo client={client} />
          </Box>
          <Typography align="center" paragraph variant="h2">
            {t('onboarding:socialsHeader')}
          </Typography>
          <Typography align="center" paragraph color="textSecondary">
            {t('onboarding:socialsSubHead')}
          </Typography>
          {!!connections.length && (
            <Box paddingTop={3}>
              <List>{renderConnections()}</List>
            </Box>
          )}
          <ConnectAccount network={Network.Linkedin}>
            <Box paddingTop={3}>
              <Button
                fullWidth
                variant="contained"
                className={classNames(classes.button, classes.linkedin)}
                onClick={() => connectionPopup(Network.Linkedin)}
                startIcon={<CustomSVG name="linkedinOutline" />}
              >
                {t('user:connectToLinkedIn')}
              </Button>
            </Box>
          </ConnectAccount>

          {hasTwitter && (
            <ConnectAccount network={Network.Twitter}>
              <Box paddingTop={2}>
                <Button
                  fullWidth
                  variant="contained"
                  className={classNames(classes.button, classes.twitter)}
                  onClick={() => connectionPopup(Network.Twitter)}
                  startIcon={<CustomSVG viewBox="0 0 21 20" name="twitterOutline" />}
                >
                  {t('user:connectToTwitter')}
                </Button>
              </Box>
            </ConnectAccount>
          )}

          <Box paddingTop={4} textAlign="center">
            {!isLoading && (
              <Link href="#" onClick={handleContinue} color="textSecondary">
                {connections.length ? t('onboarding:doneForNow') : t('onboarding:skipForNow')}
              </Link>
            )}
            {isLoading && <Loaders.Standard height={17} />}
          </Box>
        </Layouts.OnboardingBox>
      </PaddedPaper>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default OnboardingSocials;
